import { getStoredCompany } from "@/lib/company-storage"
import { evaluateCoupons, evaluateRulePromotions } from "@/lib/discount-evaluation/api"
import { isAxiosError } from "axios"
import { useAtomValue, useSetAtom, useStore, type WritableAtom } from "jotai"
import { useEffect, useRef } from "react"
import {
  couponSnapshotAtom,
  discountRetryAtom,
  rulePromotionSnapshotAtom,
  type EvaluationSnapshot,
} from "../lib/discount-plan"
import { useDiscountPlan } from "./useDiscountPlan"
import { useSettleCouponCodes } from "./useSettleCouponCodes"

const EVALUATION_DEBOUNCE_MS = 300

type SnapshotAtom<T> = WritableAtom<EvaluationSnapshot<T>, [EvaluationSnapshot<T>], void>

function isCancel(error: unknown): boolean {
  return isAxiosError(error) && error.code === "ERR_CANCELED"
}

function useEvaluation<Req, Res>(
  snapshotAtom: SnapshotAtom<Res>,
  key: string,
  request: Req | null,
  fetcher: (merchantId: string, request: Req, signal: AbortSignal) => Promise<Res>,
  retry: number,
  onReady?: (data: Res) => void,
) {
  const store = useStore()
  const latestRequest = useRef(request)
  const latestOnReady = useRef(onReady)

  useEffect(() => {
    latestRequest.current = request
    latestOnReady.current = onReady
  })

  useEffect(() => {
    const request = latestRequest.current
    if (!request || !key) return
    const current = store.get(snapshotAtom)
    if (current.status === "ready" && current.key === key) return
    const merchantId = getStoredCompany()?.id
    if (!merchantId) return

    const controller = new AbortController()
    store.set(snapshotAtom, { status: "loading", key })
    const timer = window.setTimeout(() => {
      fetcher(merchantId, request, controller.signal).then(
        (data) => {
          store.set(snapshotAtom, { status: "ready", key, data })
          latestOnReady.current?.(data)
        },
        (error: unknown) => {
          if (controller.signal.aborted || isCancel(error)) return
          console.warn("[checkout] discount evaluation failed", error)
          store.set(snapshotAtom, { status: "error", key })
        },
      )
    }, EVALUATION_DEBOUNCE_MS)

    return () => {
      window.clearTimeout(timer)
      controller.abort()
    }
  }, [key, retry, store, snapshotAtom, fetcher])
}

/**
 * Keeps the server's discount answers in step with the bag: rule promotions
 * first, then the coupons on top of them, each debounced and cancelled when
 * the bag moves on. Mounted once, by the checkout layout; the steps read the
 * answers through useDiscountPlan. Off for table orders, which have no extras.
 */
export function useDiscountEvaluation(enabled: boolean): void {
  const plan = useDiscountPlan()
  const retry = useAtomValue(discountRetryAtom)
  const settleCoupons = useSettleCouponCodes()
  useEvaluation(
    rulePromotionSnapshotAtom,
    enabled ? plan.rulePromotions.key : "",
    plan.rulePromotions.request,
    evaluateRulePromotions,
    retry,
  )
  useEvaluation(
    couponSnapshotAtom,
    enabled ? plan.coupons.key : "",
    plan.coupons.request,
    evaluateCoupons,
    retry,
    settleCoupons,
  )
}

/** Asks the server again for the bag on screen after a failed evaluation. */
export function useRetryDiscountEvaluation(): () => void {
  const bump = useSetAtom(discountRetryAtom)
  const setCoupons = useSetAtom(couponSnapshotAtom)
  const setRulePromotions = useSetAtom(rulePromotionSnapshotAtom)
  return () => {
    setCoupons({ status: "idle" })
    setRulePromotions({ status: "idle" })
    bump((value) => value + 1)
  }
}
